import React, { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRecordsForObject, createRecord, updateRecord } from '../../services/recordStorageService';
import { TabbedModal } from './TabbedModal';
import { usePageLayout } from '../../hooks/useObjectManager';
import { EditSection } from '../edit/EditSection';
import { SearchableSelect } from './SearchableSelect';
import { useToast } from '../../contexts/ToastContext';
import { ListViewRecord } from '../../types/listView';
import { User, Package, Mail, FileText, Paperclip, Link2, X } from 'lucide-react';
import { RelatedRecordsTab } from '../tabs/RelatedRecordsTab';
import { useApplicationContext } from '../../contexts/ApplicationContext';
import { searchCreditBlockAssigneeOptions } from '../../services/gobodo/creditBlockNewCaseGobodoSearch';
import { applyCaseOwnerGraphFields } from '../../services/creditBlock/creditBlockCaseCreate';
import {
  applyCreditBlockRecordTypeFields,
  findCreditBlockRecordTypeTarget,
} from '../../services/creditBlock/creditBlockCaseGraphRelations';
import { shouldUseGobodo } from '../../utils/gobodoUtils';

interface CaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseRecord?: ListViewRecord | null;
}

export const CaseModal: React.FC<CaseModalProps> = ({
  isOpen,
  onClose,
  caseRecord
}) => {
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();
  const { currentApplication } = useApplicationContext();
  const isEditMode = !!caseRecord;
  const isCreditBlock = currentApplication === 'CreditBlock';

  const { data: pageLayout, isLoading: loadingLayout } = usePageLayout('Case__c', caseRecord);

  const [formData, setFormData] = useState<Record<string, any>>({});
  const [ownerSearch, setOwnerSearch] = useState('');
  /** Raw assignee SearchableSelect value (`u:…`) for Gobodo OwnerRelField when saving. */
  const [ownerAssigneePickRaw, setOwnerAssigneePickRaw] = useState('');

  const useGobodo = shouldUseGobodo();
  const { data: ownerOptions = [] } = useQuery({
    queryKey: ['gobodo', 'UsersApi', 'searchUsers', 'caseModalOwner', ownerSearch],
    queryFn: () => searchCreditBlockAssigneeOptions(ownerSearch),
    enabled: isCreditBlock && isOpen && useGobodo,
    staleTime: 60_000,
  });

  const [notes, setNotes] = useState<string[]>([]);
  const [newNote, setNewNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: caseModalRecords } = useQuery({
    queryKey: ['caseModalRecords'],
    queryFn: async () => {
      const [clients, contacts] = await Promise.all([
        getRecordsForObject('Client__c'),
        getRecordsForObject('Contact__c')
      ]);
      return { clients, contacts };
    },
    enabled: isOpen,
    staleTime: 60 * 1000
  });
  const allContacts = useMemo(
    () => caseModalRecords?.contacts ?? [],
    [caseModalRecords]
  );

  // Populate form from record properties directly
  useEffect(() => {
    if (caseRecord) {
      const data: Record<string, any> = {};
      for (const [key, value] of Object.entries(caseRecord)) {
        if (value !== undefined && value !== null && typeof value !== 'object') {
          data[key] = value;
        }
      }
      setFormData(data);
      setNotes(caseRecord.notes as string[] || []);
    } else {
      setFormData({});
      setNotes([]);
    }
    setOwnerAssigneePickRaw('');
    setErrors({});
    setNewNote('');
  }, [caseRecord, isOpen]);

  // Reset contact when client changes
  useEffect(() => {
    if (formData.contactId) {
      const contactBelongsToClient = allContacts.some(
        c => c.id === formData.contactId && c.accountId === formData.accountId
      );
      if (!contactBelongsToClient) {
        setFormData(prev => ({ ...prev, contactId: '' }));
      }
    }
  }, [formData.accountId, formData.contactId, allContacts]);

  // Handle field changes (for dynamic fields)
  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Handle change events (legacy support)
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    handleFieldChange(name, value);
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      setNotes([...notes, newNote.trim()]);
      setNewNote('');
    }
  };

  const handleRemoveNote = (index: number) => {
    setNotes(notes.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    const subject = String(formData.subject || formData.Subject__c || '').trim();

    if (!subject) {
      newErrors.subject = 'Case subject is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveMutation = useMutation({
    mutationFn: async (data: Record<string, any> & { notes: string[]; _ownerAssigneePickRaw?: string }) => {
      const { notes: _notes, _ownerAssigneePickRaw, ...fields } = data;
      const gobodoData: Record<string, any> = {};
      for (const [key, value] of Object.entries(fields)) {
        if (value !== undefined && value !== null && value !== '' && key !== 'id' && key !== 'label') {
          gobodoData[key] = value;
        }
      }

      if (isCreditBlock && useGobodo && _ownerAssigneePickRaw?.startsWith('u:')) {
        applyCaseOwnerGraphFields(gobodoData, {
          ownerPickRaw: _ownerAssigneePickRaw,
          ownerDisplayName: String(gobodoData.owner ?? gobodoData.Owner__c ?? ''),
        });
      }

      if (isEditMode && caseRecord?.id) {
        const updated = await updateRecord('Case__c', caseRecord.id, gobodoData);
        if (!updated) throw new Error('Failed to update case');
        return { ...caseRecord, ...updated };
      }
      let recordTypeTarget = null;
      if (isCreditBlock && useGobodo) {
        recordTypeTarget = await findCreditBlockRecordTypeTarget();
        applyCreditBlockRecordTypeFields(gobodoData, recordTypeTarget);
      }
      const created = await createRecord('Case__c', gobodoData);
      return { ...data, id: created.id };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['caseListViews'] });
      queryClient.invalidateQueries({ queryKey: ['caseRecords'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['caseListViewData'] });
      queryClient.invalidateQueries({ queryKey: ['genericListViews', 'data', 'Case__c'] });
      if (isEditMode) {
        queryClient.invalidateQueries({ queryKey: ['case', caseRecord?.id] });
      }
      const subject = (data as any).Subject__c ?? (data as any).subject ?? 'Case';
      success(
        isEditMode ? 'Case updated' : 'Case created',
        isEditMode ? `${subject} has been updated` : `${subject} has been created`
      );
      onClose();
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message ?? error?.message ?? 'Failed to save case. Please try again.';
      showError('Error', typeof msg === 'string' ? msg : 'Failed to save case. Please try again.');
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      showError('Validation Error', 'Please fix the errors before submitting');
      return;
    }

    saveMutation.mutate({ ...formData, notes, _ownerAssigneePickRaw: ownerAssigneePickRaw });
  };

  // Define tabs based on mode
  // PE-1462: Removed Tasks and History tabs - activities consolidated into Activity Timeline
  const tabs = isEditMode
    ? [
        // EDIT MODE: All tabs (minus Tasks/History per PE-1462)
        { id: 'overview', label: 'Overview', icon: User },
        { id: 'related', label: 'Related', icon: Link2 },
        { id: 'products', label: 'Products', icon: Package },
        { id: 'emails', label: 'Emails', icon: Mail },
        { id: 'notes', label: 'Notes', icon: FileText },
        { id: 'files', label: 'Files', icon: Paperclip }
      ]
    : [
        // CREATE MODE: Overview only
        { id: 'overview', label: 'Overview', icon: User }
      ];

  if (isCreditBlock && isEditMode && isOpen) {
    const inputClass = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm';
    const labelClass = 'block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1';
    return (
      <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Edit Case</h2>
            <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"><X className="h-5 w-5" /></button>
          </div>
          <div className="px-6 py-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Case Number</label>
                <input type="text" readOnly value={formData.caseNumber || formData.id || ''} className={`${inputClass} bg-gray-50 dark:bg-gray-900/80`} />
              </div>
              <div>
                <label className={labelClass}>Status</label>
                <select name="status" value={formData.status || 'New'} onChange={handleChange} className={inputClass}>
                  <option value="New">New</option>
                  <option value="In Progress">In Progress</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Escalated">Escalated</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
            </div>
            <div>
              <label className={labelClass}>Subject</label>
              <input type="text" name="subject" value={formData.subject || ''} onChange={handleChange} className={inputClass} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Sub-Type</label>
                <input type="text" name="subType" value={formData.subType || formData.Sub_Type__c || ''} onChange={handleChange} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Priority</label>
                <select name="priority" value={formData.priority || 'Medium'} onChange={handleChange} className={inputClass}>
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Type</label>
                <input type="text" readOnly value={formData.type || 'Credit Block'} className={`${inputClass} bg-gray-50 dark:bg-gray-900/80`} />
              </div>
              <div>
                <label className={labelClass}>Origin</label>
                <input type="text" name="origin" value={formData.origin || ''} onChange={handleChange} className={inputClass} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Owner</label>
                <SearchableSelect
                  label=""
                  name="owner"
                  value={formData.owner || formData.Owner__c || ''}
                  onChange={(val) => {
                    const opt = ownerOptions.find((o) => o.value === val);
                    const name = opt?.label || val;
                    setOwnerAssigneePickRaw(useGobodo && val.startsWith('u:') ? val : '');
                    handleFieldChange('owner', name);
                    handleFieldChange('Owner__c', name);
                  }}
                  options={[
                    ...(formData.owner ? [{ value: formData.owner, label: formData.owner }] : []),
                    ...ownerOptions.filter((o) => o.label !== formData.owner),
                  ]}
                  skipLocalFilter={useGobodo}
                  onSearchTermChange={useGobodo ? setOwnerSearch : undefined}
                  placeholder="Search assignee..."
                />
              </div>
              <div>
                <label className={labelClass}>Client</label>
                <input type="text" readOnly value={formData.accountName || formData.AccountName__c || ''} className={`${inputClass} bg-gray-50 dark:bg-gray-900/80`} />
              </div>
            </div>
            <div>
              <label className={labelClass}>Description</label>
              <textarea name="description" value={formData.description || formData.Description__c || ''} onChange={handleChange} rows={3} className={inputClass} />
            </div>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-600 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saveMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
            >
              {saveMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <TabbedModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Case' : 'New Case'}
      tabs={tabs}
      maxWidth="2xl"
      actions={
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#334155] rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending
              ? 'Saving...'
              : isEditMode ? 'Save Changes' : 'Create Case'
            }
          </button>
        </div>
      }
    >
      {(activeTab) => (
        <form onSubmit={handleSubmit} className="space-y-6">
          {activeTab === 'overview' && (
            <div className="max-h-[60vh] overflow-y-auto pr-2">
              {loadingLayout ? (
                <div className="space-y-6">
                  <div className="animate-pulse space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i}>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2"></div>
                        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : pageLayout?.sections && pageLayout.sections.length > 0 ? (
                pageLayout.sections.map(section => (
                  <EditSection
                    key={section.id}
                    section={section}
                    objectApiName="Case__c"
                    formData={formData}
                    onChange={handleFieldChange}
                    errors={errors}
                    disabled={saveMutation.isPending}
                  />
                ))
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>No page layout configured for Case.</p>
                  <p className="text-sm mt-2">Go to Object Manager to configure page layouts.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'related' && caseRecord?.id && (
            <RelatedRecordsTab
              recordId={caseRecord.id}
              objectType="Case__c"
              recordName={caseRecord.subject as string}
            />
          )}

          {activeTab === 'products' && (
            <div className="text-center py-12">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Products
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Products associated with this case will appear here.
              </p>
            </div>
          )}

          {activeTab === 'emails' && (
            <div className="text-center py-12">
              <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Emails
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Email correspondence related to this case will appear here.
              </p>
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Add Note
                </label>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Type your note here..."
                  rows={4}
                  className="w-full px-3 py-2 bg-white dark:bg-[#0f172a] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleAddNote}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Note
                </button>
              </div>

              {notes.length > 0 ? (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Notes ({notes.length})
                  </label>
                  <div className="space-y-2">
                    {notes.map((note, index) => (
                      <div
                        key={index}
                        className="p-3 bg-gray-50 dark:bg-[#0f172a] border border-gray-200 dark:border-gray-700 rounded-lg"
                      >
                        <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{note}</p>
                        <button
                          type="button"
                          onClick={() => handleRemoveNote(index)}
                          className="text-sm text-red-600 dark:text-red-400 hover:underline mt-2"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    No notes yet. Add your first note above.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'files' && (
            <div className="text-center py-12">
              <Paperclip className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No Files
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Documents and files attached to this case will appear here.
              </p>
            </div>
          )}
        </form>
      )}
    </TabbedModal>
  );
};
